import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, List, ListItemButton, ListItemText, Typography, CircularProgress, Tabs, Tab } from '@mui/material';
import api from '../../../../../utils/apiConfig';

const templates = [
  { id: 'letter', name: 'Letter (8.5"×11")', widthPx: Math.round(8.5 * 96), heightPx: 11 * 96 },
  { id: 'legal', name: 'Legal (8.5"×14")', widthPx: Math.round(8.5 * 96), heightPx: 14 * 96 },
  { id: 'tabloid', name: 'Tabloid (11"×17")', widthPx: 11 * 96, heightPx: 17 * 96 },
  { id: 'a4', name: 'A4 (210×297mm ~ 8.27"×11.7")', widthPx: Math.round(8.27 * 96), heightPx: Math.round(11.7 * 96) },
  { id: 'square-1080', name: 'Square (1080×1080)', widthPx: 1080, heightPx: 1080 },
  { id: 'square-2048', name: 'Square (2048×2048)', widthPx: 2048, heightPx: 2048 },
];

export default function OpenDialog({ open, onClose, locationId, onOpenAsset, onOpenTemplateAsset, onApplyTemplate, loadingOpen = false }) {
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState(0); // 0 = Projects, 1 = Templates

  useEffect(() => {
    if (!open || !locationId) return;
    setLoading(true);
    api.get('/photo-ads/assets', { params: { locationId } })
      .then((res) => {
        if (res.data?.success) {
          const list = res.data.data || [];
          setAssets(list);
        }
      })
      .catch((e) => console.error('Failed to load assets', e))
      .finally(() => setLoading(false));
    setSelectedId(null);
  }, [open, locationId]);

  const projects = assets.filter((a) => a?.mimeType === 'application/vnd.dymen.photoads+json');
  const templates = assets.filter((a) => a?.mimeType === 'application/vnd.dymen.photoads.template+json');
  const visible = tab === 0 ? projects : templates;
  const selectedAsset = visible.find(a => a.id === selectedId) || null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Open</DialogTitle>
      <DialogContent dividers>
        <Box>
          <Tabs value={tab} onChange={(e, v) => { setTab(v); setSelectedId(null); }} sx={{ mb: 1 }}>
            <Tab label={`Projects (${projects.length})`} />
            <Tab label={`Templates (${templates.length})`} />
          </Tabs>
          {!locationId && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Select a location to see saved files.
            </Typography>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading…</Typography>
            </Box>
          ) : (
            <List dense onKeyDown={(e) => {
              if (loadingOpen) return;
              if (e.key === 'Enter' && selectedAsset) {
                if (tab === 0) onOpenAsset?.(selectedAsset);
                else onOpenTemplateAsset?.(selectedAsset);
              }
            }}>
              {visible.map((a) => (
                <ListItemButton
                  key={a.id}
                  selected={selectedId === a.id}
                  disabled={loadingOpen}
                  onClick={() => setSelectedId(a.id)}
                  onDoubleClick={(e) => { if (!loadingOpen) { (tab === 0 ? onOpenAsset : onOpenTemplateAsset)?.(a); } e.stopPropagation(); }}
                >
                  <ListItemText
                    primary={a.name}
                    secondary={`${a.width}×${a.height} • ${new Date(a.createdAt).toLocaleString()}`}
                  />
                </ListItemButton>
              ))}
              {visible.length === 0 && (
                <Typography color="text.secondary">{tab === 0 ? 'No projects yet.' : 'No templates yet.'}</Typography>
              )}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disabled={!selectedAsset || loadingOpen}
          onClick={() => selectedAsset && (tab === 0 ? onOpenAsset?.(selectedAsset) : onOpenTemplateAsset?.(selectedAsset))}
        >
          {loadingOpen ? 'Opening…' : 'Open'}
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
